// frontend/src/components/public/blocks/CaseStudiesBlock.tsx
import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip } from '@mui/material';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';

interface CaseStudiesBlockProps {
  data: {
    cases: Array<{
      partnerName: string;
      partnerType: 'corporate' | 'foundation' | 'government' | 'individual';
      programme: string;
      challenge: string;
      solution: string;
      outcomes: string[];
      partnerQuote?: string;
      metrics?: Array<{
        label: string;
        value: string | number;
      }>;
      images?: string[];
      year: number;
    }>;
    layout: 'detailed' | 'summary';
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const CaseStudiesBlock: React.FC<CaseStudiesBlockProps> = ({ data, theme }) => {
  const { cases = [], layout = 'detailed' } = data;
  const accentColor = theme?.accentColor || '#FF0000';
  const primaryColor = theme?.primaryColor || '#000000';

  // Safety check - ensure all case studies have required fields
  const safeCases = cases.map(caseStudy => ({
    ...caseStudy,
    outcomes: Array.isArray(caseStudy.outcomes) ? caseStudy.outcomes : [],
    metrics: Array.isArray(caseStudy.metrics) ? caseStudy.metrics : [],
    images: Array.isArray(caseStudy.images) ? caseStudy.images : []
  }));

  if (safeCases.length === 0) {
    return null;
  }

  const getPartnerTypeLabel = (type: string) => {
   const labels: Record<string, string> = {
  corporate: 'Corporate Partnership',
  foundation: 'Foundation Grant',
  government: 'Public Funding',
  individual: 'Private Philanthropy',
  cultural: 'Cultural Institution Partnership',
  community: 'Community Collaboration',
  educational: 'Academic / Educational Partnership',
  media: 'Media Partnership',
  international: 'International Collaboration',
  venue: 'Venue / Presentation Partner'
};
    return labels[type] || type;
  };

  const getPartnerTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      corporate: '#1976d2',
      foundation: '#9c27b0',
      government: '#388e3c',
      individual: '#f57c00'
    };
    return colors[type] || accentColor;
  };

  // Detailed Layout
  if (layout === 'detailed') {
    return (
      <Box
        id="case-studies"
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 3, md: 6 },
          maxWidth: '1200px',
          mx: 'auto'
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            fontWeight: 700,
            color: primaryColor,
            mb: 2,
            textAlign: 'center'
          }}
        >
          Success Stories
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: '1.05rem',
            color: 'text.secondary',
            mb: 6,
            textAlign: 'center',
            maxWidth: '700px',
            mx: 'auto'
          }}
        >
          Real partnerships that made a difference
        </Typography>

        {safeCases.map((caseStudy, index) => (
          <Card
            key={index}
            elevation={0}
            sx={{
              mb: 6,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {/* Header with image if available */}
            {caseStudy.images && caseStudy.images.length > 0 && (
              <Box
                sx={{
                  height: 300,
                  backgroundImage: `url(${caseStudy.images[0]})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
            )}

            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              {/* Partner Info */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={getPartnerTypeLabel(caseStudy.partnerType)}
                    size="small"
                    sx={{
                      bgcolor: getPartnerTypeColor(caseStudy.partnerType),
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                  <Chip
                    label={caseStudy.year}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.5rem', md: '1.875rem' },
                    fontWeight: 700,
                    color: primaryColor,
                    mb: 1
                  }}
                >
                  {caseStudy.partnerName}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '1.125rem',
                    color: accentColor,
                    fontWeight: 600
                  }}
                >
                  {caseStudy.programme}
                </Typography>
              </Box>

              {/* Challenge */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: primaryColor,
                    mb: 1
                  }}
                >
                  Challenge
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1rem',
                    lineHeight: 1.7,
                    color: 'text.secondary'
                  }}
                >
                  {caseStudy.challenge}
                </Typography>
              </Box>

              {/* Solution */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: primaryColor,
                    mb: 1
                  }}
                >
                  Solution
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1rem',
                    lineHeight: 1.7,
                    color: 'text.secondary'
                  }}
                >
                  {caseStudy.solution}
                </Typography>
              </Box>

              {/* Outcomes */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: primaryColor,
                    mb: 2
                  }}
                >
                  Outcomes
                </Typography>
                <Grid container spacing={2}>
                  {caseStudy.outcomes.map((outcome, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <TrendingUpIcon
                          sx={{
                            color: accentColor,
                            fontSize: '1.25rem',
                            mt: 0.25,
                            flexShrink: 0
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.95rem',
                            lineHeight: 1.6,
                            color: 'text.primary'
                          }}
                        >
                          {outcome}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Metrics */}
              {caseStudy.metrics && caseStudy.metrics.length > 0 && (
                <Box sx={{ mb: 4, p: 3, bgcolor: '#f9f9f9', borderRadius: 2 }}>
                  <Grid container spacing={3}>
                    {caseStudy.metrics.map((metric, idx) => (
                      <Grid item xs={6} md={3} key={idx}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: '2rem',
                            fontWeight: 800,
                            color: accentColor,
                            mb: 0.5
                          }}
                        >
                          {metric.value}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            color: 'text.secondary',
                            fontWeight: 600
                          }}
                        >
                          {metric.label}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Partner Quote */}
              {caseStudy.partnerQuote && (
                <Box
                  sx={{
                    p: 3,
                    borderLeft: `4px solid ${accentColor}`,
                    bgcolor: '#f9f9f9',
                    borderRadius: 1
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.05rem',
                      lineHeight: 1.7,
                      fontStyle: 'italic',
                      color: 'text.primary'
                    }}
                  >
                    "{caseStudy.partnerQuote}"
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  // Summary Layout
  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 3, md: 6 },
        maxWidth: '1200px',
        mx: 'auto'
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontSize: { xs: '1.75rem', md: '2.25rem' },
          fontWeight: 700,
          color: primaryColor,
          mb: 6,
          textAlign: 'center'
        }}
      >
        Success Stories
      </Typography>

      <Grid container spacing={4}>
        {safeCases.map((caseStudy, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 32px ${accentColor}22`,
                  borderColor: accentColor
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <Chip
                    label={getPartnerTypeLabel(caseStudy.partnerType)}
                    size="small"
                    sx={{
                      bgcolor: getPartnerTypeColor(caseStudy.partnerType),
                      color: 'white',
                      fontWeight: 600,
                      mb: 2
                    }}
                  />

                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: primaryColor,
                      mb: 0.5
                    }}
                  >
                    {caseStudy.partnerName}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.95rem',
                      color: accentColor,
                      fontWeight: 600
                    }}
                  >
                    {caseStudy.programme}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                    color: 'text.secondary',
                    mb: 2
                  }}
                >
                  {caseStudy.challenge}
                </Typography>

                {caseStudy.outcomes.length > 0 && (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: primaryColor,
                        mb: 1
                      }}
                    >
                      Key Outcomes:
                    </Typography>
                    {caseStudy.outcomes.slice(0, 2).map((outcome, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'flex-start',
                          mb: 0.5
                        }}
                      >
                        <TrendingUpIcon
                          sx={{
                            color: accentColor,
                            fontSize: '1rem',
                            mt: 0.25
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.85rem',
                            lineHeight: 1.5,
                            color: 'text.primary'
                          }}
                        >
                          {outcome}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CaseStudiesBlock;